import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    Form,
    Input,
    Space,
    message,
    Switch,
    Skeleton,
    Typography,
    notification,
    Tooltip,
    Popconfirm,
    Segmented
} from "antd";
import { EditOutlined, PlusOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import {
    getAllBusinessUnits,
    createBusinessUnit,
    updateBusinessUnit,
    toggleBusinessUnitStatus,
    BusinessUnit
} from "../services/businessunitservice";
import BusinessUnitDrawer from "../components/businessunits/businessunitdrawer";
import BuUsersDrawer from "../components/businessunits/buusersdrawer";
import { useAuth } from "../auth/authcontext";

const { Title } = Typography;
const { Search } = Input;

type StatusFilter = "all" | "active" | "inactive";

const BusinessUnits: React.FC = () => {
    const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
    const [filteredBUs, setFilteredBUs] = useState<BusinessUnit[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Edit Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState<BusinessUnit | null>(null);
    const [form] = Form.useForm();

    // User Assignment Drawer state
    const [usersDrawerOpen, setUsersDrawerOpen] = useState(false);
    const [selectedBuForUsers, setSelectedBuForUsers] = useState<BusinessUnit | null>(null);

    const { isReadOnly } = useAuth();

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

    const showError = (msg: string) => {
        notification.error({
            message: "Error",
            description: msg,
            placement: "topRight"
        });
    };

    const fetchBusinessUnits = async () => {
        setLoading(true);
        try {
            const data = await getAllBusinessUnits();
            setBusinessUnits(data);
            applyFilters(data, searchTerm, statusFilter);
        } catch {
            showError("Failed to load business units");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBusinessUnits();
    }, []);

    const applyFilters = (data: BusinessUnit[], search: string, status: StatusFilter) => {
        let filtered = data;

        if (search) {
            const lowerSearch = search.toLowerCase();
            filtered = filtered.filter(
                bu => bu.name.toLowerCase().includes(lowerSearch) || bu.code.toLowerCase().includes(lowerSearch)
            );
        }

        if (status === "active") {
            filtered = filtered.filter(bu => bu.active);
        } else if (status === "inactive") {
            filtered = filtered.filter(bu => !bu.active);
        }

        setFilteredBUs(filtered);
    };

    useEffect(() => {
        applyFilters(businessUnits, searchTerm, statusFilter);
    }, [searchTerm, statusFilter, businessUnits]);

    const handleSearch = (value: string) => {
        setSearchTerm(value.trim());
    };

    const openCreateDrawer = () => {
        setEditing(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const openEditDrawer = (record: BusinessUnit) => {
        setEditing(record);
        form.setFieldsValue(record);
        setDrawerOpen(true);
    };

    const openUsersDrawer = (record: BusinessUnit) => {
        setSelectedBuForUsers(record);
        setUsersDrawerOpen(true);
    };

    const onSave = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            if (editing) {
                await updateBusinessUnit(editing.guid, values);
                message.success("Business Unit updated");
            } else {
                await createBusinessUnit(values);
                message.success("Business Unit created");
            }

            setDrawerOpen(false);
            form.resetFields();
            fetchBusinessUnits();
        } catch {
            showError("Failed to save business unit");
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (guid: string, active: boolean) => {
        try {
            await toggleBusinessUnitStatus(guid, active);
            message.success("Status updated");
            fetchBusinessUnits();
        } catch {
            showError("Failed to update status");
        }
    };

    const columns = [
        { title: "Name", dataIndex: "name" },
        { title: "Code", dataIndex: "code" },
        {
            title: "Active",
            dataIndex: "active",
            render: (value: boolean, record: BusinessUnit) => (
                <Popconfirm
                    title={value ? "Disable Business Unit" : "Enable Business Unit"}
                    description={
                        value
                            ? "Are you sure you want to disable this business unit?"
                            : "Are you sure you want to enable this business unit?"
                    }
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() => toggleStatus(record.guid, !value)}
                    disabled={isReadOnly}
                >
                    <Switch
                        checked={value}
                        size="small"
                        onClick={(_, e) => e.preventDefault()}
                        disabled={isReadOnly}
                    />
                </Popconfirm>
            )
        },
        {
            title: "Actions",
            render: (_: any, record: BusinessUnit) => (
                <Space>
                    <Tooltip title="Manage Users">
                        <Button
                            icon={<UsergroupAddOutlined style={{ color: "var(--color-primary)" }} />}
                            onClick={() => openUsersDrawer(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            icon={<EditOutlined style={{ color: "var(--color-primary)" }} />}
                            onClick={() => openEditDrawer(record)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Business Units</Title>

            <Space style={{ marginBottom: 16 }} wrap>
                <Search
                    placeholder="Search by name or code..."
                    allowClear
                    style={{ maxWidth: 400 }}
                    onSearch={handleSearch}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <Segmented
                    value={statusFilter}
                    onChange={(val) => setStatusFilter(val as StatusFilter)}
                    options={[
                        { label: "All", value: "all" },
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" }
                    ]}
                />

                <Button
                    color="primary"
                    variant="filled"
                    icon={<PlusOutlined />}
                    onClick={openCreateDrawer}
                >
                    Add Business Unit
                </Button>
            </Space>

            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <Table
                    rowKey="guid"
                    columns={columns}
                    dataSource={filteredBUs}
                    pagination={{
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                        showTotal: (total, range) =>
                            `Showing ${range[0]}–${range[1]} of ${total} BUs`
                    }}
                />
            )}

            <BusinessUnitDrawer
                open={drawerOpen}
                editing={editing}
                form={form}
                onClose={() => setDrawerOpen(false)}
                onSave={onSave}
                saving={saving}
            />

            <BuUsersDrawer
                open={usersDrawerOpen}
                businessUnit={selectedBuForUsers}
                onClose={() => setUsersDrawerOpen(false)}
            />
        </div>
    );
};

export default BusinessUnits;
